if (!customElements.get('team-order-builder')) {
  const DRAFT_STORAGE_KEY = 'caliburn:team-order-draft';
  // Engraved top line when the customer leaves Team Label blank.
  const DEFAULT_LABEL = 'Caliburn Bat Co.';

  // Team Design fields rendered by blocks in the product-information
  // section above. Read by input name so this section doesn't need to
  // share a form with them.
  const FIELD = {
    model: 'properties[Model]',
    barrel: 'properties[Barrel Color]',
    handle: 'properties[Handle Color]',
    logoColor: 'properties[Logo Color]',
    teamLogo: 'properties[Team Logo]',
    label: 'properties[Engraving Line1]',
  };

  // Every team order uses the product's Multi-Color Logo variants (all
  // variants in a bat group share one price), since there's no single vs
  // multi color choice anymore.
  const LOGO_TIER = '2';

  const TEAM_LOGO_LABELS = {
    'Customer will send separately': 'Sending later',
  };

  class TeamOrderBuilder extends HTMLElement {
    connectedCallback() {
      this.variantMap = this.readJSON('[data-team-order-variant-map]');
      this.rosterRows = this.querySelector('[data-team-roster-rows]');
      this.rowTemplate = this.querySelector('[data-team-row-template]');
      this.notesField = this.querySelector('[data-team-order-notes]');
      this.notesCounter = this.querySelector('[data-team-notes-counter]');
      this.reviewDialog = this.querySelector('[data-team-review-dialog]');
      this.reviewSummary = this.querySelector('[data-team-review-summary]');
      this.reviewTotal = this.querySelector('[data-team-review-total]');
      this.statusEl = this.querySelector('[data-team-status]');
      this.inlineStatusEl = this.querySelector('[data-team-inline-status]');
      this.discountEl = this.querySelector('[data-team-discount]');

      this.minBats = Number(this.dataset.minBats || 3);
      this.logoFee = Number(this.dataset.logoFee || 50);
      this.sizes = this.parseList(this.dataset.sizes);
      this.fungoSizes = this.parseList(this.dataset.fungoSizes);
      this.productUrl = this.dataset.productUrl || '';
      this.money = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      });

      this.draftId = null;
      this.rowSeq = 0;
      this.persistTimer = null;
      this.renderQueued = false;

      this.querySelector('[data-team-add-row]')?.addEventListener('click', () => {
        const row = this.addRow();
        row?.querySelector('[data-team-row-engraving]')?.focus();
        this.changed();
      });

      this.querySelector('[data-team-step-jump]')?.addEventListener('click', () => {
        this.designAnchor()?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      this.notesField?.addEventListener('input', () => this.changed());

      this.querySelector('[data-team-review-open]')?.addEventListener('click', () => this.openReview());
      this.querySelectorAll('[data-team-review-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.reviewDialog?.close());
      });
      this.reviewDialog?.addEventListener('close', () => this.render());
      this.querySelector('[data-team-submit]')?.addEventListener('click', () => this.submitOrder());

      // Any change in the Team Design blocks refreshes the summary.
      this.onDesignChange = (event) => {
        if (this.contains(event.target)) return;
        if (event.type === 'change' && event.target.name === FIELD.model) this.updateAllRowSizeOptions();
        this.changed();
      };
      ['input', 'change', 'caliburn:color-swatch-change', 'caliburn:logo-color-change', 'caliburn:team-logo-change'].forEach(
        (type) => document.addEventListener(type, this.onDesignChange)
      );

      this.rehydrate();

      // Team orders start at the minimum; also pads older drafts saved with fewer rows.
      while (this.rosterRows && this.rows().length < this.minBats) this.addRow();

      // Links like ?model=Fungo (e.g. from the Teams page) preselect a model.
      this.preselectModel(new URLSearchParams(window.location.search).get('model'));

      this.render();
    }

    // Matches the model code at the start of an option label, so "DC" picks
    // "DC - Balanced • ..." and "Fungo" picks "Fungo - For coaches • ...".
    preselectModel(code) {
      const select = this.modelSelect();
      if (!code || !select) return;
      const wanted = code.trim().toLowerCase();
      const option = Array.from(select.options).find((opt) => {
        const label = opt.value.toLowerCase();
        return label && (label === wanted || label.startsWith(`${wanted} `));
      });
      if (!option) return;
      select.value = option.value;
      this.updateAllRowSizeOptions();
    }

    disconnectedCallback() {
      ['input', 'change', 'caliburn:color-swatch-change', 'caliburn:logo-color-change', 'caliburn:team-logo-change'].forEach(
        (type) => document.removeEventListener(type, this.onDesignChange)
      );
    }

    readJSON(selector) {
      const el = this.querySelector(selector);
      if (!el) return {};
      try {
        return JSON.parse(el.textContent) || {};
      } catch (err) {
        console.error('team-order-builder: failed to parse', selector, err);
        return {};
      }
    }

    parseList(value) {
      return (value || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }

    changed() {
      this.queueRender();
      this.schedulePersist();
    }

    // ---------------------------------------------------------------
    // Team Design field access
    // ---------------------------------------------------------------

    field(name) {
      return document.querySelector(`[name="${CSS.escape(name)}"]:not([type="radio"])`);
    }

    value(name) {
      const radio = document.querySelector(`[name="${CSS.escape(name)}"][type="radio"]:checked`);
      if (radio) return radio.value;
      return this.field(name)?.value.trim() || '';
    }

    modelSelect() {
      return this.field(FIELD.model);
    }

    designAnchor() {
      return document.querySelector('.team-design-card') || this.modelSelect();
    }

    currentModelTier() {
      const select = this.modelSelect();
      return select?.selectedOptions[0]?.dataset.tier || '';
    }

    swatchHex(propertyKey) {
      const swatch = document.querySelector(
        `color-property-picker[data-property-key="${propertyKey}"] [aria-pressed="true"]`
      );
      return swatch?.style.getPropertyValue('--swatch-color').trim() || '';
    }

    logoColorHex() {
      return document.querySelector('logo-color-picker [aria-pressed="true"]')?.dataset.logoColorHex || '';
    }

    design() {
      return {
        model: this.value(FIELD.model),
        barrel: this.value(FIELD.barrel),
        handle: this.value(FIELD.handle),
        logoColor: this.value(FIELD.logoColor),
        teamLogo: this.value(FIELD.teamLogo),
        label: this.value(FIELD.label),
      };
    }

    teamLogoLabel(value) {
      if (!value) return '';
      return TEAM_LOGO_LABELS[value] || 'Uploaded';
    }

    // ---------------------------------------------------------------
    // Roster rows
    // ---------------------------------------------------------------

    addRow(data) {
      if (!this.rowTemplate || !this.rosterRows) return null;
      const row = this.rowTemplate.content.cloneNode(true).querySelector('[data-team-row]');
      this.rowSeq += 1;
      row.dataset.rowId = data?.rowId || `row-${Date.now()}-${this.rowSeq}`;

      const engravingInput = row.querySelector('[data-team-row-engraving]');
      const sizeSelect = row.querySelector('[data-team-row-size]');

      this.populateSizeOptions(sizeSelect);

      if (data) {
        // Drafts saved by V1 stored playerName + engravingLine2 separately.
        engravingInput.value = data.engraving ?? (data.engravingLine2 || data.playerName || '');
        if (data.size) sizeSelect.value = data.size;
      }

      engravingInput.addEventListener('input', () => this.changed());
      sizeSelect.addEventListener('change', () => {
        row.classList.remove('team-order-roster__row--missing-size');
        this.changed();
      });

      row.querySelector('[data-team-row-delete]')?.addEventListener('click', () => {
        if (this.rows().length <= this.minBats) return;
        row.remove();
        this.changed();
      });

      this.rosterRows.appendChild(row);
      return row;
    }

    rows() {
      return Array.from(this.rosterRows?.querySelectorAll('[data-team-row]') || []);
    }

    populateSizeOptions(select) {
      const sizes = this.currentModelTier() === 'E' ? this.fungoSizes : this.sizes;
      const current = select.value;
      select.replaceChildren(new Option('Size', ''));
      sizes.forEach((size) => select.appendChild(new Option(size, size)));
      if (sizes.includes(current)) select.value = current;
    }

    updateAllRowSizeOptions() {
      this.rosterRows?.querySelectorAll('[data-team-row-size]').forEach((select) => this.populateSizeOptions(select));
    }

    getRosterData() {
      return this.rows().map((row) => ({
        rowId: row.dataset.rowId,
        engraving: row.querySelector('[data-team-row-engraving]')?.value.trim() || '',
        size: row.querySelector('[data-team-row-size]')?.value || '',
      }));
    }

    // ---------------------------------------------------------------
    // Summary, steps, pricing
    // ---------------------------------------------------------------

    resolveVariant() {
      const tier = this.currentModelTier();
      if (!tier) return null;
      return this.variantMap[`${tier}-${LOGO_TIER}`] || null;
    }

    // Bats at the variant's flat price. The per-bat team logo fee is shown
    // and then waived, since every team order meets the bat minimum.
    pricing(count) {
      const variant = this.resolveVariant();
      if (!variant) return null;
      const unit = variant.price / 100;
      const fee = this.logoFee;
      const bats = unit * count;
      const logoFees = fee * count;
      const discount = count >= this.minBats ? logoFees : 0;
      return { unit, fee, bats, logoFees, discount, subtotal: bats + logoFees - discount };
    }

    renderCosts(list, count, pricing) {
      list.replaceChildren();
      if (!pricing || !count) return;
      const rows = [
        [`Bats (${count} x ${this.money.format(pricing.unit)})`, this.money.format(pricing.bats)],
        [`Team logo fee (${count} x ${this.money.format(pricing.fee)})`, this.money.format(pricing.logoFees)],
      ];
      if (pricing.discount) {
        rows.push([`Team discount (${this.minBats}+ bats)`, `-${this.money.format(pricing.discount)}`, 'discount']);
      }
      rows.forEach(([term, value, modifier]) => {
        const wrap = document.createElement('div');
        if (modifier) wrap.className = `team-order-summary__cost--${modifier}`;
        const dt = document.createElement('dt');
        const dd = document.createElement('dd');
        dt.textContent = term;
        dd.textContent = value;
        wrap.append(dt, dd);
        list.appendChild(wrap);
      });
    }

    queueRender() {
      if (this.renderQueued) return;
      this.renderQueued = true;
      requestAnimationFrame(() => {
        this.renderQueued = false;
        this.render();
      });
    }

    render() {
      const rows = this.rows();
      rows.forEach((row, index) => {
        const indexEl = row.querySelector('[data-team-row-index]');
        if (indexEl) indexEl.textContent = index + 1;
      });

      const design = this.design();
      const count = rows.length;
      const pricing = this.pricing(count);

      this.setSummary('count', String(count));
      this.setSummary('model', design.model);
      this.setSummary('barrel', design.barrel, this.swatchHex('Barrel Color'));
      this.setSummary('handle', design.handle, this.swatchHex('Handle Color'));
      this.setSummary('logo-color', design.logoColor, this.logoColorHex());
      this.setSummary('team-logo', this.teamLogoLabel(design.teamLogo));
      this.setSummary('label', design.label || DEFAULT_LABEL);
      const costs = this.querySelector('[data-team-costs]');
      if (costs) this.renderCosts(costs, count, pricing);
      this.setSummary('subtotal', this.money.format(pricing ? pricing.subtotal : 0));

      this.renderDiscount(count);

      const atMinimum = rows.length <= this.minBats;
      rows.forEach((row) => {
        const button = row.querySelector('[data-team-row-delete]');
        if (!button) return;
        button.disabled = atMinimum;
        button.title = atMinimum ? `Team orders need at least ${this.minBats} bats` : '';
      });

      if (this.notesCounter && this.notesField) {
        this.notesCounter.textContent = `${this.notesField.value.length}/${this.notesField.maxLength}`;
      }

      const reviewing = this.reviewDialog?.open;
      this.setStep(1, design.model ? 'complete' : 'current');
      this.setStep(2, reviewing ? 'complete' : design.model ? 'current' : 'upcoming');
      this.setStep(3, reviewing ? 'current' : 'upcoming');
    }

    setSummary(key, text, hex) {
      const el = this.querySelector(`[data-summary="${key}"]`);
      if (!el) return;
      el.replaceChildren();
      if (hex) {
        const dot = document.createElement('span');
        dot.className = 'team-order-summary__dot';
        dot.style.background = hex;
        el.appendChild(dot);
      }
      const span = document.createElement('span');
      if (text) {
        span.textContent = text;
      } else {
        span.textContent = 'Not selected';
        span.className = 'team-order-summary__empty';
      }
      el.appendChild(span);
    }

    setStep(number, state) {
      const step = this.querySelector(`[data-team-step="${number}"]`);
      if (!step) return;
      step.dataset.state = state;
      if (state === 'current') {
        step.setAttribute('aria-current', 'step');
      } else {
        step.removeAttribute('aria-current');
      }
    }

    renderDiscount(count) {
      if (!this.discountEl) return;
      this.discountEl.dataset.state = 'included';
      this.discountEl.querySelector('[data-team-discount-title]').textContent = 'Your team logo is included!';
      this.discountEl.querySelector('[data-team-discount-text]').textContent = `Team orders waive the ${this.money.format(
        this.logoFee
      )} per bat logo fee. You save ${this.money.format(this.logoFee * count)}.`;
    }

    // ---------------------------------------------------------------
    // Persistence (edit-until-checkout)
    // ---------------------------------------------------------------

    schedulePersist() {
      clearTimeout(this.persistTimer);
      this.persistTimer = setTimeout(() => this.persist(), 300);
    }

    ensureDraftId() {
      if (!this.draftId) {
        this.draftId = window.crypto?.randomUUID ? crypto.randomUUID() : `draft-${Date.now()}`;
      }
      return this.draftId;
    }

    persist() {
      const design = this.design();
      const draft = {
        draftId: this.ensureDraftId(),
        updatedAt: Date.now(),
        productUrl: this.productUrl,
        teamDesign: {
          model: design.model,
          barrelColor: design.barrel,
          handleColor: design.handle,
          logoColor: design.logoColor,
          teamLogo: design.teamLogo,
          engravingLine1: design.label,
          notes: this.notesField?.value || '',
        },
        roster: this.getRosterData(),
      };
      try {
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
      } catch (err) {
        console.error('team-order-builder: could not save draft', err);
      }
    }

    loadDraft() {
      try {
        const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (err) {
        return null;
      }
    }

    rehydrate() {
      const requestedDraftId = new URLSearchParams(window.location.search).get('edit_draft');
      const draft = this.loadDraft();
      if (!draft) return;
      if (requestedDraftId && draft.draftId !== requestedDraftId) return;

      this.draftId = draft.draftId;
      const design = draft.teamDesign || {};

      const select = this.modelSelect();
      if (select && design.model) select.value = design.model;

      // Clicking the saved swatch keeps each picker's own UI (pressed
      // state, selected label, logo preview) in sync with the value.
      this.pressSwatch(`color-property-picker[data-property-key="Barrel Color"] [data-color-property-swatch]`, design.barrelColor);
      this.pressSwatch(`color-property-picker[data-property-key="Handle Color"] [data-color-property-swatch]`, design.handleColor);
      this.pressSwatch('logo-color-picker [data-logo-color-swatch]', design.logoColor, 'logoColorSwatch');

      if (design.teamLogo) {
        customElements.whenDefined('team-logo-upload').then(() => {
          document.querySelector('team-logo-upload')?.restore(design.teamLogo);
        });
      }

      const label = this.field(FIELD.label);
      if (label && design.engravingLine1) {
        label.value = design.engravingLine1;
        label.dispatchEvent(new Event('input', { bubbles: true }));
      }

      if (this.notesField && design.notes) this.notesField.value = design.notes;

      if (Array.isArray(draft.roster)) draft.roster.forEach((row) => this.addRow(row));
    }

    pressSwatch(selector, value, datasetKey = 'colorPropertySwatch') {
      if (!value) return;
      const button = Array.from(document.querySelectorAll(selector)).find((b) => b.dataset[datasetKey] === value);
      button?.click();
    }

    // ---------------------------------------------------------------
    // Review + submit
    // ---------------------------------------------------------------

    validate() {
      const errors = [];
      const select = this.modelSelect();
      const selectWrapper = select?.closest('.team-model-select');
      selectWrapper?.classList.remove('team-model-select--missing');

      if (!select?.value) {
        errors.push({ message: 'Select a bat model to continue.', target: select });
        selectWrapper?.classList.add('team-model-select--missing');
      }

      const rows = this.rows();

      const missingSize = rows.filter((row) => !row.querySelector('[data-team-row-size]')?.value);
      rows.forEach((row) => row.classList.toggle('team-order-roster__row--missing-size', missingSize.includes(row)));
      if (missingSize.length) {
        errors.push({
          message: `Choose a bat size for ${missingSize.length === 1 ? 'bat' : 'bats'} #${missingSize
            .map((row) => rows.indexOf(row) + 1)
            .join(', #')}.`,
          target: missingSize[0].querySelector('select'),
        });
      }

      if (select?.value && !this.resolveVariant()) {
        errors.push({ message: 'This model and logo type combination is not available. Call us and we can help.' });
      }

      if (rows.length < this.minBats) {
        errors.push({ message: `Team orders need at least ${this.minBats} bats.` });
      }

      return errors;
    }

    openReview() {
      const errors = this.validate();
      if (errors.length) {
        this.setStatus(this.inlineStatusEl, errors[0].message, 'error');
        const target = errors.find((e) => e.target)?.target;
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          target.focus({ preventScroll: true });
        }
        return;
      }
      this.setStatus(this.inlineStatusEl, '', null);

      const design = this.design();
      const roster = this.getRosterData();
      const count = roster.length;
      const pricing = this.pricing(count);

      const details = [
        ['Bat Model', design.model],
        ['Barrel Color', design.barrel],
        ['Handle Color', design.handle],
        ['Caliburn Logo Color', design.logoColor],
        ['Team Logo', this.teamLogoLabel(design.teamLogo) || 'None provided'],
        ['Team Label', design.label || DEFAULT_LABEL],
      ];

      const list = document.createElement('dl');
      list.className = 'team-order-summary__list';
      details.forEach(([term, value]) => {
        const wrap = document.createElement('div');
        const dt = document.createElement('dt');
        const dd = document.createElement('dd');
        dt.textContent = term;
        dd.textContent = value || 'Not selected';
        wrap.append(dt, dd);
        list.appendChild(wrap);
      });

      const table = document.createElement('table');
      table.className = 'team-order-review__table';
      table.innerHTML = '<thead><tr><th>#</th><th>Player / Engraving</th><th>Bat Size</th></tr></thead>';
      const tbody = document.createElement('tbody');
      roster.forEach((row, index) => {
        const tr = tbody.insertRow();
        tr.insertCell().textContent = index + 1;
        tr.insertCell().textContent = row.engraving || 'No engraving';
        tr.insertCell().textContent = row.size;
      });
      table.appendChild(tbody);

      this.reviewSummary.replaceChildren(list, table);

      const costs = document.createElement('dl');
      costs.className = 'team-order-summary__costs';
      this.renderCosts(costs, count, pricing);
      const subtotal = document.createElement('div');
      subtotal.className = 'team-order-summary__subtotal';
      const subtotalLabel = document.createElement('span');
      subtotalLabel.textContent = 'Subtotal';
      const subtotalValue = document.createElement('span');
      subtotalValue.textContent = this.money.format(pricing.subtotal);
      subtotal.append(subtotalLabel, subtotalValue);
      this.reviewTotal.replaceChildren(costs, subtotal);

      this.setStatus(this.statusEl, '', null);
      this.reviewDialog.showModal();
      this.render();
    }

    setStatus(el, message, kind) {
      if (!el) return;
      el.textContent = message;
      if (kind) {
        el.dataset.status = kind;
      } else {
        delete el.dataset.status;
      }
    }

    async submitOrder() {
      const errors = this.validate();
      if (errors.length) {
        this.setStatus(this.statusEl, errors[0].message, 'error');
        return;
      }

      const submitButton = this.querySelector('[data-team-submit]');
      if (submitButton) submitButton.disabled = true;
      this.setStatus(this.statusEl, 'Adding your team order to cart...', null);

      try {
        const draftId = this.ensureDraftId();
        await this.removeExistingDraftLines(draftId);

        const variant = this.resolveVariant();
        const design = this.design();

        const items = this.getRosterData().map((row, index) => ({
          id: variant.id,
          quantity: 1,
          properties: {
            _draft_id: draftId,
            _row_id: row.rowId,
            _sort_order: index + 1,
            Model: design.model,
            'Bat Size': row.size,
            'Engraving Line1': design.label,
            'Engraving Line2': row.engraving,
            'Barrel Color': design.barrel,
            'Handle Color': design.handle,
            'Logo Color': design.logoColor,
            'Team Logo': design.teamLogo,
          },
        }));

        // Checkout doesn't show the cart note, so the notes also ride on the
        // first bat where they appear in checkout, emails and the order.
        const note = this.notesField?.value.trim();
        if (note && items.length) items[0].properties['Order Notes'] = note;

        // The cart lists the newest line first, so add the roster in
        // reverse to have it read 1, 2, 3 in the cart.
        const addResponse = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [...items].reverse() }),
        });

        if (!addResponse.ok) {
          const errorBody = await addResponse.json().catch(() => ({}));
          throw new Error(errorBody.description || 'Could not add your team order to the cart.');
        }

        if (note) {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ note }),
          });
        }

        // Keep the draft so "Edit Team Order" from the cart can reopen it.
        this.persist();
        this.setStatus(this.statusEl, 'Added! Taking you to your cart...', 'success');
        window.location.href = '/cart';
      } catch (err) {
        console.error('team-order-builder: submit failed', err);
        this.setStatus(this.statusEl, err.message || 'Something went wrong. Please try again.', 'error');
        if (submitButton) submitButton.disabled = false;
      }
    }

    async removeExistingDraftLines(draftId) {
      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();
      const updates = {};
      (cart.items || [])
        .filter((item) => item.properties?._draft_id === draftId)
        .forEach((item) => {
          updates[item.key] = 0;
        });

      if (!Object.keys(updates).length) return;

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
    }
  }

  customElements.define('team-order-builder', TeamOrderBuilder);
}
